import {
  FieldAutomationDataType,
  FieldMappingConfig,
  IngestionConfigSchema,
  IngestionSourceType,
} from '../../../services/queries/FileIngestionQueries';

/**
 * FD-3697 — Data Type axis. The runner keys off legacy fields (rowStructure,
 * integrationDataDomain, mapping targetTypes); these helpers derive the data type on
 * read and write the legacy fields through on save, so no backend change is required.
 */

export const DATA_TYPE_LABELS: Record<FieldAutomationDataType, string> = {
  'entity-properties': 'Entity Properties',
  'company-financials': 'Company Financials (KPIs)',
  commentary: 'Performance Commentary',
};

export const DATA_TYPE_HELPER_TEXT: Record<FieldAutomationDataType, string> = {
  'entity-properties': 'Point-in-time values written to entity fields or custom fields.',
  'company-financials': 'Periodic financials (month/quarter/year), optionally budget vs actual.',
  commentary: 'Narrative text stored per company and reporting period.',
};

/** Financials and commentary carry a reporting period; entity properties never do. */
export function dataTypeHasPeriod(dataType: FieldAutomationDataType): boolean {
  return dataType === 'company-financials' || dataType === 'commentary';
}

/** Data types the source's runner can execute (commentary: file + snowflake only). */
export function availableDataTypes(sourceType: IngestionSourceType | undefined): FieldAutomationDataType[] {
  if (sourceType === 'file' || sourceType === 'snowflake') {
    return ['entity-properties', 'company-financials', 'commentary'];
  }
  return ['entity-properties', 'company-financials'];
}

/**
 * Derive the data type from a persisted config; a persisted `dataType` wins. Two legacy
 * quirks shape the rule order: an explicit layout rowStructure outranks a stray KPI
 * mapping — except on API configs, where the old form persisted 'row-per-entity' as
 * residue — and Snowflake's blanket 'row-per-entity-period' default means only mappings
 * are trusted there.
 */
export function deriveDataType(config: IngestionConfigSchema): FieldAutomationDataType {
  if (config.dataType) {
    return config.dataType;
  }
  const mappings = config.fieldMappings ?? [];
  const rowStructure = config.fileLevel?.rowStructure;
  if (mappings.some((m) => m.targetType === 'commentary')) {
    return 'commentary';
  }
  if (
    config.sourceType !== 'api-integration' &&
    (rowStructure === 'row-per-entity' || rowStructure === 'multiple-rows-per-entity')
  ) {
    return 'entity-properties';
  }
  if (mappings.some((m) => m.targetType === 'kpi')) {
    return 'company-financials';
  }
  if (
    config.sourceType === 'api-integration' &&
    (config.integrationDataDomain === 'metrics' ||
      (config.integrationSync?.metricMappings ?? []).some((m) => m.targetType === 'kpi'))
  ) {
    return 'company-financials';
  }
  if (rowStructure === 'row-per-entity-period' && mappings.length === 0) {
    return 'company-financials';
  }
  return 'entity-properties';
}

/** Clamp to what the source can run, e.g. a commentary config whose source switched to API. */
export function reconcileDataType(
  sourceType: IngestionSourceType | undefined,
  dataType: FieldAutomationDataType
): FieldAutomationDataType {
  return availableDataTypes(sourceType).includes(dataType) ? dataType : 'entity-properties';
}

/**
 * Targets NEW mappings may use. Existing off-list mappings are grandfathered by the
 * mapping UI; form validity requires the final set to agree with the data type.
 */
export function allowedTargetTypes(
  dataType: FieldAutomationDataType
): NonNullable<FieldMappingConfig['targetType']>[] {
  if (dataType === 'commentary') {
    return ['commentary'];
  }
  if (dataType === 'company-financials') {
    return ['kpi'];
  }
  return ['entity-field', 'custom-field'];
}

/**
 * Write the data type through to the legacy runner fields. Never mutates the input and
 * never strips mappings. Entity-properties saves normalize rowStructure to a pure layout
 * and drop kpiMetadata; grandfathered API domains (documents, notes, …) are preserved —
 * only empty domains or this UI's own 'metrics'/'customColumns' stamps are rewritten.
 */
export function writeThroughDataType(config: IngestionConfigSchema): IngestionConfigSchema {
  const dataType = config.dataType ?? deriveDataType(config);
  const next: IngestionConfigSchema = { ...config, dataType };

  if (dataTypeHasPeriod(dataType)) {
    next.fileLevel = { ...next.fileLevel, rowStructure: 'row-per-entity-period' };
  } else {
    const layout = next.fileLevel?.rowStructure;
    next.fileLevel = {
      ...next.fileLevel,
      rowStructure: layout === 'multiple-rows-per-entity' ? layout : 'row-per-entity',
      kpiMetadata: undefined,
    };
  }

  if (config.sourceType === 'api-integration') {
    const domain = config.integrationDataDomain;
    const isOwnStamp = !domain || domain === 'metrics' || domain === 'customColumns';
    if (isOwnStamp) {
      next.integrationDataDomain = dataType === 'company-financials' ? 'metrics' : 'customColumns';
    }
  }

  return next;
}
